// Действия под ответом: скопировать и оценить. SMY-123 · P8 Э8.5
//
// РЯД СТОИТ ПОД КАЖДЫМ ОТВЕТОМ, а не появляется по расписанию. Это правка от
// 07.09.2026: первая версия показывала оценку только у каждого пятого запуска
// — того, у которого сервер решил спросить, — и человек, захотевший сказать
// «не то», чаще всего не находил, куда нажать. Спрашивать не каждый раз и
// давать возможность не каждый раз — разные вещи, и первая версия перепутала
// их.
//
// ВОПРОСА СЛОВАМИ ЗДЕСЬ БОЛЬШЕ НЕТ (07.09.2026). Рядом с кнопками стояла
// подпись «Подошёл результат?» — по расписанию сервера (`ratings.askEveryNth`,
// SMY-98). Пока кнопки были голыми значками, она объясняла, что это вообще
// такое; с подписями «Подошло» и «Не подошло» рядом она стала третьим текстом
// в ряду, который спрашивает то, на что две кнопки слева уже отвечают.
//
// Расписание при этом осталось на сервере и приезжает в `askRating` — просто
// кабинет его больше не читает. Убирать его вместе с подписью было бы правкой
// продукта, а не экрана: частота настраивается из админки, и решать за неё
// здесь нечем.
//
// ПОДПИСИ У КНОПОК ПОЯВИЛИСЬ 07.09.2026. До этого ряд состоял из трёх голых
// иконок, и объяснялось это тем, что слова «Подошло / Не подошло» под каждым
// ответом читаются как часть ответа. На экране вышло иначе: три одинаково
// серых значка не говорят, что случится по нажатию, а «палец вниз»
// отличается от «скопировать» только формой — то есть ровно тем, что
// приходится разглядывать. Слово ответу не спорит: ряд по-прежнему приглушён
// до наведения и стоит подписью под ним, а не действием экрана.
//
// Формы заведены в набор парой и зеркальны — иначе кнопка, которая заметнее,
// собирала бы больше нажатий, чем заслужила. С подписями то же правило
// перешло на слова: «Подошло» и «Не подошло» набраны одинаково, без цвета и
// без выделения на той, которую хотелось бы услышать чаще.

import { useState } from 'react'
import { RATING_REASONS, RATING_REASON_LABELS, type RatingReason, type RatingVerdict } from '@smyslika/core'

import { Chip } from './controls.tsx'
import { Icon } from './Icon.tsx'
import { plainAnswer } from '@smyslika/core'

export interface AnswerActionsProps {
  /**
   * Ответ, который копируют, — РАЗМЕТКОЙ, как он пришёл от модели. Чистый
   * текст из него делается на нажатии, а не здесь: разбор на каждой
   * перерисовке ленты — работа впустую у каждой записи, кроме одной.
   *
   * Пусто — кнопки копии нет: копировать нечего.
   */
  text?: string | undefined
  /** Что уже поставлено. Пусто — ещё не оценивали. */
  rating?: { verdict: RatingVerdict; reason: RatingReason | null } | undefined
  onRate: (input: { verdict: RatingVerdict; reason?: RatingReason; comment?: string }) => Promise<void> | void
}

export function AnswerActions({ text, rating, onRate }: AnswerActionsProps) {
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Причина спрашивается ПОСЛЕ нажатия «не подошло», а не вместо него: у
  // человека, которому некогда объяснять, надо взять хотя бы главное.
  const [asking, setAsking] = useState(false)
  const [comment, setComment] = useState('')

  async function copy() {
    if (text == null) return
    setFailed(null)
    try {
      // В БУФЕР УХОДИТ ТО, ЧТО НА ЭКРАНЕ: без звёздочек и решёток, но с
      // разделами и пунктами. Правка от 07.09.2026; до неё копировался
      // исходник модели, и «**Разминка**» приезжала звёздочками в письмо,
      // куда ответ и вставляют. Правила не свои — те же блоки, которыми
      // ответ нарисован (`plainAnswer`).
      //
      // Буфер обмена доступен не всегда: в небезопасном контексте и при
      // отказе в разрешении `writeText` бросает. Молчать нельзя — человек
      // нажал и уйдёт вставлять то, чего не скопировалось.
      await navigator.clipboard.writeText(plainAnswer(text))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setFailed('Не удалось скопировать. Выдели текст и скопируй вручную.')
    }
  }

  async function rate(input: { verdict: RatingVerdict; reason?: RatingReason; comment?: string }) {
    setBusy(true)
    setFailed(null)
    try {
      await onRate(input)
      // Уточнение раскрывается только у плохой оценки и только когда причина
      // ещё не названа: второй раз спрашивать то же самое незачем.
      setAsking(input.verdict === 'bad' && input.reason == null)
    } catch {
      setFailed('Оценка не сохранилась. Нажми ещё раз.')
    } finally {
      setBusy(false)
    }
  }

  const verdict = rating?.verdict
  return (
    <div className="answer-actions">
      <div className="answer-actions__row">
        {text != null && (
          <button type="button" className="answer-actions__btn" onClick={() => void copy()}>
            {/* Подтверждение меняет и значок, и слово. Соседи от этого не
                прыгают: «Скопировать» и «Скопировано» отличаются на полторы
                точки — замерено на стенде, а не предположено. */}
            <Icon name={copied ? 'tick' : 'copy'} size={19} strokeWidth={1.9} />
            <span className="answer-actions__label">{copied ? 'Скопировано' : 'Скопировать'}</span>
          </button>
        )}

        {/* Имя для программы чтения с экрана даёт сама подпись — `aria-label`
            рядом с ней был бы вторым именем того же места. */}
        <button
          type="button"
          className="answer-actions__btn"
          disabled={busy}
          aria-pressed={verdict === 'good'}
          onClick={() => void rate({ verdict: 'good' })}
        >
          <Icon name="thumbUp" size={19} strokeWidth={1.9} />
          <span className="answer-actions__label">Подошло</span>
        </button>

        <button
          type="button"
          className="answer-actions__btn"
          disabled={busy}
          aria-pressed={verdict === 'bad'}
          onClick={() => void rate({ verdict: 'bad' })}
        >
          <Icon name="thumbDown" size={19} strokeWidth={1.9} />
          <span className="answer-actions__label">Не подошло</span>
        </button>

        {/* «Спасибо, учтём» — не вопрос, а ответ на нажатие, и остаётся:
            оценка уходит на сервер молча, и без этой строки человек не знает,
            дошла ли она. */}
        {verdict != null && <span className="answer-actions__ask">Спасибо, учтём.</span>}
      </div>

      {failed != null && <p className="answer-actions__error">{failed}</p>}

      {verdict === 'bad' && asking && (
        <div className="answer-actions__why">
          <div className="answer-actions__row">
            {RATING_REASONS.map((reason) => (
              <Chip
                key={reason}
                selected={rating?.reason === reason}
                disabled={busy}
                onClick={() => void rate({ verdict: 'bad', reason, ...(comment !== '' ? { comment } : {}) })}
              >
                {RATING_REASON_LABELS[reason]}
              </Chip>
            ))}
          </div>
          <textarea
            className="field answer-actions__comment"
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Что именно не так — необязательно"
            onBlur={() => {
              if (comment !== '') {
                void rate({
                  verdict: 'bad',
                  ...(rating?.reason != null ? { reason: rating.reason } : {}),
                  comment,
                })
              }
            }}
          />
        </div>
      )}
    </div>
  )
}
